import { Classes, Divider, H3, H4, H5 } from '@blueprintjs/core';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Colors } from './variables';
import { AppColors } from './variables-slykit';

/**
 * flexbox shorthands
 */
export const flexChildrenV = css`
  display: flex;
  flex-direction: column;
`;
export const flexChildrenH = css`
  display: flex;
  flex-direction: row;
`;
export const flexStay = css`
	flex: 0 0 auto;
`;
export const flexAdapt = css`
  flex: 1 1 auto;
  overflow: hidden;
`;

/**
 * Center things
 * OR: https://css-tricks.com/centering-css-complete-guide/
 */
export const centerSelf = css`
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  position: relative;
`;
export const centerContent = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const centerContentVertically = css`
  display: flex;
  align-items: center;
`;

/**
 * Aligns a <Button minimal />'s visible content to the edge of its parent container, rather than its :hover hit-zone
 * doesn't work so great with icon only Buttons
 */
export const alignedMinimalButtonRight = css`
	margin-right: -10px;
	&.${Classes.SMALL}{
		margin-right: -7px;
	}
	&.${Classes.LARGE}{
		margin-right: -15px;
	}
`;
export const alignedMinimalButtonLeft = css`
	margin-left: -10px;
	&.${Classes.SMALL}{
		margin-left: -7px;
	}
	&.${Classes.LARGE}{
		margin-left: -15px;
	}
`;
export const alignedMinimalButton = css`
	${alignedMinimalButtonRight}
	${alignedMinimalButtonLeft}
`;

/**
 * this is a blueprint Divider without default `margin`
 */
export const DividerFull = styled(Divider)`
	margin: 0;
`;

/**
 * FlexSplitter will split the siblings before and after it in a display:flex; parent
 */
export const FlexSplitter = styled.div`
	flex: 1 1 auto;
`;

/**
 * Headers without Margins
 */
export const HeaderSm = styled(H5)`
	margin: 0;
`;
export const HeaderMd = styled(H4)`
	margin: 0;
`;
export const HeaderLg = styled(H3)`
	margin: 0;
`;

/**
 * Clamps text elements to a specified number of lines
 * giving the element a max-height equal to a multiple of its line-height
 */
export const clampLineCount = (
	lineCount: number = 4,
	lineHeight: number = 1.25, // em
) => css`
	line-height: ${lineHeight}em !important; // important to override bp3 css
	max-height: ${lineCount * lineHeight}em;
	overflow: hidden;
`;

/**
 * text will ellipsis on one line instead of wrapping...
 */
export const overflowEllipsis = css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	word-wrap: normal;
`;

/**
 * interactive list item backgrounds
 */
export const selectedClassName = 'interactiveListItemSelected';
export const interactiveListItem = css`
	cursor: pointer;

	// transitions might be nice here?

	&:hover {
		background-color: ${AppColors.interactiveBgHover};
		border-color: ${Colors.LIGHT_GRAY2};
	}

	&:active {
		background-color: ${AppColors.interactiveBgActive};
		border-color: ${Colors.LIGHT_GRAY1};
	}

	&.${selectedClassName}, &.selected {
		background-color: ${AppColors.interactiveBgSelected};
		border-color: ${AppColors.PT_INTENT_PRIMARY};
	}
`;

/**
 * Hover to a parent to reveal children
 * give the parent a css={[hoverRevealChildrenType]} and the child a className={hoverRevealClassName}
 */
export const hoverRevealClassName = 'hoverReveal';
export const hoverRevealChildrenVisibility = css`
	& .${hoverRevealClassName} {
		visibility: hidden;
	}
	&:hover,
	&.${selectedClassName} {
		.${hoverRevealClassName} {
			visibility: visible;
		}
	}
`;
export const hoverRevealChildrenDisplay = css`
	&:not(:hover){
		.${hoverRevealClassName} {
			display: none;
		}
	}
`;
